import { observer } from "mobx-react-lite";
import { useTran } from "@/hooks";
import { SCOPE_KEYS, SCOPE_LABELS } from "../../../domains/scope";
import { dashboardEChartsStore } from "../../../stores/e-charts.store";
import { colorForIndex, SCOPE_COLOR, TOTAL_COLOR } from "./colors";
import { pickRankingYear, sortEntitiesByTotalDesc } from "./tooltip-rows";

const EChartsLegend = observer(
  ({ entities, years, entityNameById, entityColorById }) => {
    const { t } = useTran();
    const store = dashboardEChartsStore;
    if (entities.length === 0) return null;

    if (store.consolidated) {
      const names = entities.map(
        (id) => entityNameById.get(String(id)) ?? String(id),
      );
      return (
        <div className="flex flex-wrap items-center justify-center gap-3 px-1">
          <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <span
                className="h-2.5 w-2.5 rounded-sm"
                style={{ backgroundColor: TOTAL_COLOR }}
              />
              {t("Total")}
            </span>
            {SCOPE_KEYS.map((scope) => (
              <span key={scope} className="inline-flex items-center gap-1.5">
                <span
                  className="h-2.5 w-2.5 rounded-sm"
                  style={{ backgroundColor: SCOPE_COLOR[scope] }}
                />
                {t(SCOPE_LABELS[scope])}
              </span>
            ))}
          </div>
          <span className="text-[11px] text-muted-foreground/80">
            {t("Consolidated")}: {names.join(", ")}
          </span>
        </div>
      );
    }

    // Legend ranked by the newest selected year that has any data, biggest
    // first, so the key itself answers "which is highest" (R09 item 4). A
    // boundary can extend years past the last submission (e.g. to 2036), so
    // the newest year alone can be all-null and would silently fall back to
    // picker order. Bar order and colors stay as picked; the fallback color
    // keeps each entity's original positional color so it still matches the
    // bars when the map is absent.
    const rankingYear = pickRankingYear(years, (year) =>
      entities.some((id) => store.totalFor(id, year) != null),
    );
    const orderedEntities =
      rankingYear === undefined
        ? entities
        : sortEntitiesByTotalDesc(entities, (id) =>
            store.totalFor(id, rankingYear),
          );

    return (
      <div className="flex flex-wrap items-center justify-center gap-3 px-1">
        {orderedEntities.map((id) => (
          <span
            key={id}
            className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground"
          >
            <span
              className="h-2.5 w-2.5 rounded-sm"
              style={{
                backgroundColor:
                  entityColorById?.get(String(id)) ??
                  colorForIndex(entities.indexOf(id)),
              }}
            />
            {entityNameById.get(String(id)) ?? String(id)}
          </span>
        ))}
      </div>
    );
  },
);

export default EChartsLegend;
